import { useEffect, useState } from 'react'
import gql from 'graphql-tag'
import { useCheckout } from 'lib/modules/checkout'
import { loadScript } from 'lib/core/resources/helpers'
import { apolloClient } from 'lib/core/services/magento/client'
import { jsonToGraphQLQuery } from 'lib/core/resources/helpers/jsonToGraphql'

const win: any = window

interface FetchAuthNetCredentials {
  storeConfig: {
    authorize_net_sandbox_mode: boolean
    authorize_net_login_id: boolean
    authorize_net_client_key: boolean
  }
}

const fetchAuthNetCredentials: FetchAuthNetCredentials = {
  storeConfig: {
    authorize_net_sandbox_mode: true,
    authorize_net_login_id: true,
    authorize_net_client_key: true
  }
}

interface AuthNetCredentials {
  authorize_net_sandbox_mode: boolean
  authorize_net_login_id: string
  authorize_net_client_key: string
}

const createGetCheckoutConfigQuery = async () => {
  return apolloClient.query({
    query: gql`{${jsonToGraphQLQuery(fetchAuthNetCredentials)}}`,
    fetchPolicy: 'no-cache'
  })
}

let clientId = ''
let loginId = ''

export const useAuthNet = () => {
  const { lockCheckout, unlockCheckout } = useCheckout()
  const [storeConfig, setStoreConfig] = useState<AuthNetCredentials | undefined>()
  const [intervalId, setIntervalId] = useState<number | undefined>(undefined)
  const [apiIsLoading, setApiIsLoading] = useState(!getIsAuthLoaded())
  const isAuthLoaded = getIsAuthLoaded()

  useEffect(() => {
    createGetCheckoutConfigQuery().then((result) => {
      setStoreConfig(result.data.storeConfig)
      clientId = result.data.storeConfig.authorize_net_client_key
      loginId = result.data.storeConfig.authorize_net_login_id
    })
  }, [])

  useEffect(() => {
    if (!win.Accept && storeConfig) {
      loadScript({
        src:
          !storeConfig.authorize_net_sandbox_mode
            ? 'https://js.authorize.net/v1/Accept.js'
            : 'https://jstest.authorize.net/v1/Accept.js',
        async: true
      })
    }
  }, [storeConfig])

  useEffect(() => {
    function checkAuth () {
      if (getIsAuthLoaded()) {
        setApiIsLoading(false)
      }
    }

    if (!intervalId && !isAuthLoaded) {
      const id = window.setInterval(checkAuth, CHECK_AUTH_API_LOADED_INTERVAL)
      setIntervalId(id)
    }

    if (isAuthLoaded) {
      window.clearInterval(intervalId)
    }

    return () => {
      window.clearInterval(intervalId)
    }
  }, [
    isAuthLoaded,
    intervalId,
    setIntervalId,
    setApiIsLoading
  ])

  const authorizeCardAndPlaceOrder = async ({ values }: AuthCardAndPlaceOrderData) => {
    const { number, expMonth, expYear, cvv } = values

    const secureData = {
      authData: {
        clientKey: clientId,
        apiLoginID: loginId
      },
      cardData: {
        cardNumber: number,
        month: expMonth,
        year: expYear,
        cardCode: cvv
      }
    }

    lockCheckout()

    const dispatchData = async (
      authorizationNumber: number = 0,
      authorizations: Authorization[] = []
    ): Promise<Authorization[]> => {
      return new Promise((resolve, reject) => {
        win.Accept?.dispatchData(secureData, (response: AuthNetData) => {
          if (response.messages.resultCode !== RESPONSE_OK) {
            unlockCheckout()
            const errorMsg = response.messages.message[0] ? response.messages.message[0].text : ''
            reject(new Error(errorMsg))
          } else {
            const opaqueData = {
              dataDescriptor: response.opaqueData.dataDescriptor,
              dataValue: response.opaqueData.dataValue
            }
            const updatedAuthorizations = [
              ...authorizations,
              {
                cardExpMonth: expMonth,
                cardExpYear: `20${expYear}`,
                opaque_data: JSON.stringify(opaqueData)
              }
            ]
            unlockCheckout()
            resolve(updatedAuthorizations)
          }
        })
      })
    }

    try {
      return dispatchData()
    } catch (e) {
      unlockCheckout()
      return false
    }
  }

  return {
    authorizeCardAndPlaceOrder,
    isLoading: apiIsLoading
  }
}

const getIsAuthLoaded = () => win.Accept

interface OpaqueData {
  dataDescriptor: string
  dataValue: string
}

interface Message {
  code: string
  text: string
}

interface Messages {
  resultCode: string
  message: [Message]
}

interface AuthNetData {
  opaqueData: OpaqueData
  messages: Messages
}

interface CreditCardData {
  number: string
  expMonth: string
  expYear: string
  cvv: string
}

interface AuthCardAndPlaceOrderData {
  values: CreditCardData
}

interface Authorization {
  cardExpMonth: string
  cardExpYear: string
  opaque_data: string
}

const RESPONSE_OK = 'Ok'
const CHECK_AUTH_API_LOADED_INTERVAL = 500
